import React, { useContext, useState } from 'react'
import * as R from 'ramda'

import { SurveyJS } from '~/components/PreStyled'

import { Animation, Box, Loading, Button, Stack, Heading, Center } from '@myxplor/stardust'

import tickAnimation from '@myxplor/stardust/dist/animations/tick.json'

import useFormSubmit from '~/utils/useFormSubmit'
import NavComponent from './NavComponent'
import ErrorMessage from '~/components/ErrorMessage'
import { TokenContext } from '../../context/TokenContext'
import { injectPrimaryCarerInfoPill } from './primaryCarerInfoPill'

const CustomSurveyRenderer = ({
  serviceId,
  childId = null,
  schema,
  initialData = {},
  readOnly,
  singlePage,
  progressBar,
  isReEnrolment,
  guid
}) => {
  const [submission, submitForm, resetSubmission] = useFormSubmit()
  const hasSubmitted = submission.loading || submission.data || submission.error
  const [currentData, setCurrentData] = useState(initialData)
  const [token] = useContext(TokenContext)

  let survey = {}

  const registerCustomCss = {}

  // Wrapper function to setup survey.onComplete event handler
  const setupOnComplete = survey => {
    // onComplete function gets passed to the form when validations pass and form is submitted
    survey.onComplete.add(completed_survey => {
      // Set 'currentData' to the survey's current state (for re-render)
      // This is because the form re-renders when 'submission' changes, and we need the data to persist
      setCurrentData(completed_survey.data)

      // Submit with the data and schema etc. Result will update 'submission'
      // Which will re-render the error/success state
      submitForm(schema, completed_survey.data, serviceId, childId, token, guid)

      survey.clear(false, false)
    })

    return survey
  }

  const setConfigurationOptions = survey => {
    // when clearInvisibleValues is set to 'none', it'll include the invisible PC_Address values into the survey data(json).
    survey.clearInvisibleValues = 'none'
    survey.showQuestionNumbers = 'off'
    survey.completedHtml = '<h3>Thanks! Your form has been submitted!</h3>'
    survey.showNavigationButtons = 'none'
    // survey.logo = 'none'
    survey.showTitle = false

    // set recaptcha to required, regardless of schema isRequired
    const Captcha = survey.getQuestionByName('Xplor_Captcha')
    Captcha && (Captcha.isRequired = true)

    return survey
  }

  const setEddr = survey => {
    const Eddr = survey.getQuestionByName('XplorRequired_EddrCheck')
    Eddr && (Eddr.isRequired = true)
    Eddr && Eddr.setPropertyValue('service_id', serviceId)

    const validateCheck = questionValue => {
      const eddrQuestionValue = questionValue && questionValue[0]

      if (eddrQuestionValue.is_enabled === false) {
        return true
      } else {
        return eddrQuestionValue.is_checked
      }
    }

    SurveyJS.FunctionFactory.Instance.register('validateCheck', validateCheck)

    return survey
  }

  const injectInitialData = survey => {
    survey.data = currentData

    return survey
  }

  const setWaitlistSpecificOptions = survey => {
    // For now, if initialData is passed in, we assume a waitlist or readonly form,
    // So we'll set the Primary Carer's Email to be ReadOnly, if it's present already
    if (initialData && !R.isEmpty(initialData) && childId) {
      const PCEmail = survey.getQuestionByName('XplorRequired_PCEmail')

      PCEmail && (PCEmail.readOnly = true)
    }

    return survey
  }

  const setReadOnly = survey => {
    if (readOnly) {
      const eddrQuestion = survey.getQuestionByName('XplorRequired_EddrCheck')
      eddrQuestion && eddrQuestion.parent.removeQuestion(eddrQuestion)

      const captcha = survey.getQuestionByName('Xplor_Captcha')
      captcha && captcha.parent.removeQuestion(captcha)

      survey.mode = 'display'
    }

    return survey
  }

  const setSinglePage = survey => {
    survey.isSinglePage = singlePage ? true : false

    return survey
  }

  const setProgressBar = survey => {
    survey.showProgressBar = progressBar ? 'top' : 'off'

    return survey
  }

  const setUniqueCssClasses = survey => {
    survey.onUpdateQuestionCssClasses.add((_survey, options) => {
      const classes = options.cssClasses
      const question = options.question

      if (question.name.startsWith('XplorRequired_')) {
        classes.root = `${classes.root} ${question.name}`
      }
    })

    return survey
  }

  const setBookingDetails = survey => {
    if (isReEnrolment) {
      const bookingDetails = survey.getPanelByName('Xplor_BookingDetailsPanel')
      bookingDetails && bookingDetails.parent.removeElement(bookingDetails)
    }

    return survey
  }

  const setReEnrolmentFields = survey => {
    if (isReEnrolment && !readOnly) {
      const newData = {}
      const consentQuestions = [
        'Xplor_EC_Consent',
        'Xplor_EC2_Consent',
        'Xplor_PC_Consent',
        'Xplor_Xplor_AC_Consent'
      ]

      for (const questionName in currentData) {
        if (consentQuestions.includes(questionName)) {
          newData[questionName] = getConsentValues(currentData[questionName])
          continue
        } else {
          if (currentData.hasOwnProperty(questionName) && currentData[questionName] !== null) {
            newData[questionName] = currentData[questionName].value
          }
        }

        const surveyQuestion = survey.getQuestionByName(questionName)

        if (surveyQuestion != null && currentData.hasOwnProperty(questionName)) {
          if (currentData[questionName].editable) {
            setReadOnlyQuestion(surveyQuestion, false)
          } else {
            setReadOnlyQuestion(surveyQuestion, true)
          }
        }

        const eddrQuestion = survey.getQuestionByName('XplorRequired_EddrCheck')
        eddrQuestion && eddrQuestion.parent.removeQuestion(eddrQuestion)
      }

      survey.data = newData
    }

    return survey
  }

  const setReadOnlyQuestion = (question, isEnabled) => {
    if (question.getType() === 'dropdown') {
      question.choicesEnableIf = `${!isEnabled}` // surveyjs expression
    } else {
      question.readOnly = isEnabled
    }
  }

  const getConsentValues = consent => {
    const consentValue = []

    for (const consentQuestionName in consent) {
      if (consent[consentQuestionName] && consent[consentQuestionName].value === 1) {
        consentValue.push(consentQuestionName)
      }
    }

    return consentValue
  }

  const defaultRenderDropdowns = survey => {
    survey.getAllQuestions().forEach(question => {
      if (question.getType() === 'dropdown') {
        question.renderAs = 'select'
      }
    })

    return survey
  }

  const setupModel = () => {
    // Creates a new SurveyJS survey object pre-configured with the passed-in schema
    survey = new SurveyJS.Model(schema)

    R.pipe(
      setConfigurationOptions, // General SurveyJS Setup
      setupOnComplete, // Set the server side validation and onComplete functions
      injectInitialData, // If initialData has been supplied - inject it into the form (waitlist and reader)
      setWaitlistSpecificOptions, // Set parent_email to read only if waitlisted etc
      setProgressBar, // Show progress bar if progressBar supplied - for renderer
      defaultRenderDropdowns, // Special fix for surveyjs dropdowns to render as normal dropdowns instead of div
      setReadOnly, // Set to ReadOnly if readOnly supplied - for reader
      setSinglePage, // Collapse multi page to single page if singlePage specified - for reader
      setUniqueCssClasses, // Set unique css classes for XplorRequired fields, for testing,
      setEddr, //sets EDDR question if enabled for service
      setReEnrolmentFields, // allow certain fields to be editable - controlled by re-enrolment api
      setBookingDetails, // Remove Booking Details Panel for re-enrolment forms
      s => injectPrimaryCarerInfoPill(s, { singlePage, readOnly }) // Info pill atop Primary Carer panel
    )(survey)

    return survey
  }

  return (
    <Box width={1} padding="medium">
      <Box>
        {/* Submission response handlers (loading, data = success, error = fail) */}
        {submission.loading && <Loading />}
        {submission.data && (
          <Stack align="center" width={1}>
            <Box height="250px" width="250px">
              <Animation data={tickAnimation} />
            </Box>
            <Box>
              <Heading align="center" level={5}>
                {submission.data?.data?.msg && !submission.data?.data?.payment_form_url
                  ? submission.data.data.msg
                  : 'Thanks! Your form has been submitted!'}
              </Heading>
            </Box>
          </Stack>
        )}
        {submission.error && (
          <Center>
            <ErrorMessage width={1 / 2}>
              <Stack space="small" align="center">
                <Box>{submission.error}</Box>
                <Button color="primary" onClick={resetSubmission}>
                  Review Form
                </Button>
              </Stack>
            </ErrorMessage>
          </Center>
        )}
      </Box>
      <Box>
        {/* Show an error if we have no schema) */}
        {!schema && <ErrorMessage width={1 / 2}>Error: Schema not provided</ErrorMessage>}

        {/* Show the survey if we have schema and have yet to submit the form */}
        {schema && !hasSubmitted && (
          <Box>
            <SurveyJS.Survey model={setupModel()} css={registerCustomCss} />
            {!readOnly && (
              <NavComponent
                survey={survey}
                schema={schema}
                serviceId={serviceId}
                childId={childId}
                isReEnrolment={isReEnrolment}
                guid={guid}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default CustomSurveyRenderer
